import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const SubstationsOverview = () => {
  const [lilSubstations, setLilSubstations] = useState([]);
  const [lilNetworks, setLilNetworks] = useState([]);
  const [meters, setMeters] = useState([]);
  const [meterReadings, setMeterReadings] = useState([]);

  const [expandedSubstationId, setExpandedSubstationId] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const substationIdFromParams = searchParams.get("substationId");

  // Загружаем данные при монтировании
  useEffect(() => {
    async function fetchData() {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [subsRes, netsRes, metersRes, readsRes] = await Promise.all([
          fetch("/api/lilsubstations", { headers }),
          fetch("/api/lilnetworks", { headers }),
          fetch("/api/meters", { headers }),
          fetch("/api/meterreadings", { headers }),
        ]);

        const [subs, nets, mets, reads] = await Promise.all([
          subsRes.json(),
          netsRes.json(),
          metersRes.json(),
          readsRes.json(),
        ]);

        setLilSubstations(subs);
        setLilNetworks(nets);
        setMeters(mets);
        setMeterReadings(reads);

        // Если в URL есть ?substationId= - "раскрываем" данную подстанцию
        if (substationIdFromParams) {
          setExpandedSubstationId(Number(substationIdFromParams));
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [substationIdFromParams]);

  // Раскрыть/Свернуть подстанцию + прописать или очистить substationId в URL
  const toggleSubstation = (id) => {
    if (expandedSubstationId === id) {
      setExpandedSubstationId(null);
      navigate(""); // очистим query-параметры
    } else {
      setExpandedSubstationId(id);
      navigate(`?substationId=${id}`);
    }
  };

  // Список «приходящих» линий для подстанции subId:
  // - классический случай: net.to_substation === subId
  // - для двусторонней (mode="twoway"), если net.from_substation === subId, тоже считаем её «приходящей»
  const getIncomingLines = (subId) => {
    return lilNetworks.filter((net) => {
      if (net.to_substation === subId) return true;
      if (net.mode === "twoway" && net.from_substation === subId) return true;
      return false;
    });
  };

  // Список «исходящих» линий:
  // - классический: net.from_substation === subId
  // - для двусторонней: если net.to_substation === subId, считаем ее тоже исходящей
  const getOutgoingLines = (subId) => {
    return lilNetworks.filter((net) => {
      if (net.from_substation === subId) return true;
      if (net.mode === "twoway" && net.to_substation === subId) return true;
      return false;
    });
  };

  // Находим последнее показание для счётчика meterId (при желании сортируем по дате)
  const getLastReadingByMeterId = (meterId) => {
    const relevant = meterReadings
      .filter((r) => r.meter === meterId)
      .sort((a, b) => b.id - a.id);
    return relevant[0] || null;
  };

  // Рендерим таблицу incoming/outgoing линий + счётчиков
  // directionType = "incoming" или "outgoing"
  const renderNetworksTable = (subId, networks, directionType) => {
    return (
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Название сети</th>
            <th>Счётчик</th>
            <th>Последнее показание</th>
            <th>
              {directionType === "incoming" ? "Откуда приходит" : "Куда уходит"}
            </th>
          </tr>
        </thead>
        <tbody>
          {networks.map((net) => {
            // Определяем, какие счетчики нужны в этом разделе:
            // - Если subId == from_substation, то:
            //   -> outgoing => показываем direction='forward'
            //   -> incoming => direction='reverse'
            // - Если subId == to_substation, то:
            //   -> incoming => direction='forward'
            //   -> outgoing => direction='reverse'
            let neededDirection = null;
            if (net.from_substation === subId) {
              // Подстанция = from
              if (directionType === "outgoing") {
                neededDirection = "forward";
              } else {
                neededDirection = "reverse";
              }
            } else if (net.to_substation === subId) {
              // Подстанция = to
              if (directionType === "incoming") {
                neededDirection = "forward";
              } else {
                neededDirection = "reverse";
              }
            }
            // Если почему-то не подходит ни один вариант (редко), пусть будет null.

            // Фильтруем счётчики по network_id + substation_id + direction
            const lineMeters = meters
              .filter(
                (m) => m.network_id === net.id && m.substation_id === subId
              )
              .filter((m) => {
                // Если neededDirection===null, мы теоретически можем вернуть все
                // или вообще пусто. Зависит от бизнес-логики.
                if (!neededDirection) return true;
                return m.direction === neededDirection;
              });

            // Партнёрская подстанция, чтобы вывести её название/ссылку
            const partnerId =
              net.from_substation === subId
                ? net.to_substation
                : net.from_substation;
            const partnerSub = lilSubstations.find((s) => s.id === partnerId);

            // Если нет счетчиков, выведем пустую строку
            if (!lineMeters.length) {
              return (
                <tr key={net.id}>
                  <td>{net.name}</td>
                  <td>—</td>
                  <td>—</td>
                  <td>
                    {partnerSub ? (
                      <button
                        className="btn btn-link"
                        onClick={() =>
                          navigate(`?substationId=${partnerSub.id}`)
                        }
                      >
                        {partnerSub.name}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            }

            // Иначе рендерим все соответствующие счётчики
            return lineMeters.map((meter) => {
              const lastReading = getLastReadingByMeterId(meter.id);
              return (
                <tr key={`${net.id}-${meter.id}`}>
                  <td>{net.name}</td>
                  <td>
                    {meter.number} ({meter.direction})
                  </td>
                  <td>{lastReading ? lastReading.value : "—"}</td>
                  <td>
                    {partnerSub ? (
                      <button
                        className="btn btn-link"
                        onClick={() =>
                          navigate(`?substationId=${partnerSub.id}`)
                        }
                      >
                        {partnerSub.name}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            });
          })}
        </tbody>
      </table>
    );
  };

  if (loading) return <div>Загрузка...</div>;

  return (
    <div className="container my-4">
      <h1>Подстанции</h1>
      <ul className="list-unstyled">
        {lilSubstations.map((sub) => {
          const isExpanded = expandedSubstationId === sub.id;
          const incoming = getIncomingLines(sub.id);
          const outgoing = getOutgoingLines(sub.id);

          return (
            <li key={sub.id} className="mb-3">
              {/* Заголовок для разворачивания/сворачивания */}
              <div
                className="bg-light p-2"
                style={{ cursor: "pointer" }}
                onClick={() => toggleSubstation(sub.id)}
              >
                {/* Подстанция {sub.id} {isExpanded ? "▲" : "▼"} - {sub.name} */}
                {sub.name} {isExpanded ? "▲" : "▼"} 
              </div>

              {isExpanded && (
                <div className="p-3 border">
                  <h5>Приходящие линии</h5>
                  {renderNetworksTable(sub.id, incoming, "incoming")}

                  <h5>Исходящие линии</h5>
                  {renderNetworksTable(sub.id, outgoing, "outgoing")}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SubstationsOverview;
